import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CustomTextfield from '../../../uiParts/form/CustomTextfield';
import {
  webhookDeliveryStatus,
  webhookDeliveryStatusLabel,
  webhookEventTypeLabel,
} from '../../../../constants/enum';
import {
  WEBHOOK_DELETE_BUTTON,
  WEBHOOK_DELETE_CONFIRM,
  WEBHOOK_DIALOG_CLOSE_BUTTON,
  WEBHOOK_ENABLED_LABEL,
  WEBHOOK_LAST_DELIVERY_NONE,
  WEBHOOK_LAST_DELIVERY_TITLE,
  WEBHOOK_SAVE_BUTTON,
  WEBHOOK_SECRET_COPIED,
  WEBHOOK_SECRET_COPY_BUTTON,
  WEBHOOK_SECRET_LABEL,
  WEBHOOK_SECRET_LAST4_DESCRIPTION,
  WEBHOOK_SECRET_PLAINTEXT_NOTICE,
  WEBHOOK_SECRET_PLAINTEXT_TITLE,
  WEBHOOK_SECRET_ROTATE_BUTTON,
  WEBHOOK_TEST_ERROR_REASON_LABEL,
  WEBHOOK_TEST_SEND_BUTTON,
  WEBHOOK_URL_HTTPS_ERROR,
  WEBHOOK_URL_LABEL,
  WEBHOOK_URL_PLACEHOLDER,
  WEBHOOK_URL_REQUIRED_ERROR,
} from '../../../../constants/labels';
import {
  RecentWebhookDeliveryType,
  WebhookEndpointFormValues,
  WebhookEndpointType,
} from '../../../../types/webhook';
import { formLabelWrapper, regularSm } from '../../../../constants/location';
import {
  gray5,
  gray60,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../../constants/colors';
import dayjs from 'dayjs';

const styles = {
  fieldLabel: {
    ...formLabelWrapper,
    color: gray80,
    textAlign: 'left' as const,
  },
  buttonLarge: {
    height: '44px',
    padding: '0 16px',
    whiteSpace: 'nowrap' as const,
  },
  buttonSmall: {
    height: '36px',
    padding: '0 12px',
    whiteSpace: 'nowrap' as const,
  },
  secretBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'monospace',
    backgroundColor: gray5,
    padding: '8px 12px',
    borderRadius: '4px',
    wordBreak: 'break-all' as const,
  },
};

const isValidHttpsUrl = (raw: string): boolean => {
  if (!raw) return false;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export interface WebhookEndpointFormProps {
  initialEndpoint: WebhookEndpointType | null;
  plaintextSecret: string | null;
  recentDelivery: RecentWebhookDeliveryType | null;
  saving: boolean;
  testing: boolean;
  onSave: (values: WebhookEndpointFormValues) => Promise<void>;
  onRotate: () => Promise<void>;
  onDelete: () => Promise<void>;
  onTestSend?: () => Promise<void>;
  onClearPlaintextSecret: () => void;
}

const WebhookEndpointForm: React.FC<WebhookEndpointFormProps> = ({
  initialEndpoint,
  plaintextSecret,
  recentDelivery,
  saving,
  testing,
  onSave,
  onRotate,
  onDelete,
  onTestSend,
  onClearPlaintextSecret,
}) => {
  const [enabled, setEnabled] = useState<boolean>(initialEndpoint?.enabled ?? false);
  const [url, setUrl] = useState<string>(initialEndpoint?.url ?? '');
  const [urlError, setUrlError] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    setEnabled(initialEndpoint?.enabled ?? false);
    setUrl(initialEndpoint?.url ?? '');
  }, [initialEndpoint]);

  const validate = (): boolean => {
    let valid = true;
    if (!url) {
      setUrlError(WEBHOOK_URL_REQUIRED_ERROR);
      valid = false;
    } else if (!isValidHttpsUrl(url)) {
      setUrlError(WEBHOOK_URL_HTTPS_ERROR);
      valid = false;
    } else {
      setUrlError('');
    }
    return valid;
  };

  const handleSave = async (): Promise<void> => {
    if (!validate()) return;
    await onSave({ enabled, url });
  };

  const handleCopySecret = async (): Promise<void> => {
    if (!plaintextSecret) return;
    try {
      await navigator.clipboard.writeText(plaintextSecret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // クリップボード API が使えない環境では無視
    }
  };

  return (
    <Stack spacing={3}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e): void => setEnabled(e.target.checked)}
            disabled={saving}
          />
        }
        label={
          <Typography sx={{ ...regularSm, color: gray100 }}>{WEBHOOK_ENABLED_LABEL}</Typography>
        }
      />

      <Box>
        <CustomTextfield
          labelText={WEBHOOK_URL_LABEL}
          value={url}
          onChange={(e): void => setUrl(e.target.value)}
          placeholder={WEBHOOK_URL_PLACEHOLDER}
          disabled={saving}
          error={!!urlError}
          helperText={urlError}
          inputWidth="100%"
          height="44px"
          inputPaddingIs10px8px
          fontSizeIs14px
        />
      </Box>

      {initialEndpoint && (
        <Box>
          <Typography sx={styles.fieldLabel}>{WEBHOOK_SECRET_LABEL}</Typography>
          {plaintextSecret ? (
            <Stack spacing={1}>
              <Typography sx={{ ...regularSm, color: red50 }}>
                {WEBHOOK_SECRET_PLAINTEXT_NOTICE}
              </Typography>
              <Typography sx={{ ...regularSm, color: gray100 }}>
                {WEBHOOK_SECRET_PLAINTEXT_TITLE}
              </Typography>
              <Box sx={styles.secretBox}>
                <Typography sx={{ flex: 1, wordBreak: 'break-all', ...regularSm, color: gray100 }}>
                  {plaintextSecret}
                </Typography>
                <Tooltip title={copied ? WEBHOOK_SECRET_COPIED : WEBHOOK_SECRET_COPY_BUTTON}>
                  <IconButton size="small" onClick={handleCopySecret}>
                    <ContentCopyIcon fontSize="small" sx={{ color: gray80 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box>
                <Button
                  onClick={onClearPlaintextSecret}
                  sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                >
                  {WEBHOOK_DIALOG_CLOSE_BUTTON}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontFamily: 'monospace', color: gray80 }}>
                ...{initialEndpoint.secret_last4}
              </Typography>
              <Typography sx={{ ...regularSm, color: gray60 }}>
                {WEBHOOK_SECRET_LAST4_DESCRIPTION}
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={onRotate}
                disabled={saving}
                sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
              >
                {WEBHOOK_SECRET_ROTATE_BUTTON}
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {initialEndpoint && (
        <Box>
          <Typography sx={styles.fieldLabel}>{WEBHOOK_LAST_DELIVERY_TITLE}</Typography>
          {recentDelivery ? (
            <Stack spacing={0.5}>
              <Typography sx={{ ...regularSm, color: gray100 }}>
                {dayjs(recentDelivery.created_at).format('YYYY-MM-DD HH:mm:ss')}
                {' / '}
                {webhookEventTypeLabel[recentDelivery.event_type]}
                {' / '}
                {webhookDeliveryStatusLabel[recentDelivery.status] ?? ''}
                {recentDelivery.http_status !== null ? ` (HTTP ${recentDelivery.http_status})` : ''}
              </Typography>
              {recentDelivery.status === webhookDeliveryStatus.failed &&
                recentDelivery.error_reason && (
                  <Typography sx={{ ...regularSm, color: red50 }}>
                    {WEBHOOK_TEST_ERROR_REASON_LABEL}: {recentDelivery.error_reason}
                  </Typography>
                )}
            </Stack>
          ) : (
            <Typography sx={{ ...regularSm, color: gray80 }}>
              {WEBHOOK_LAST_DELIVERY_NONE}
            </Typography>
          )}
        </Box>
      )}

      <Stack
        direction="row"
        spacing={2}
        sx={{ flexWrap: 'wrap', rowGap: '12px', alignItems: 'center' }}
      >
        <Button
          onClick={handleSave}
          disabled={saving}
          sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
        >
          {WEBHOOK_SAVE_BUTTON}
        </Button>
        {onTestSend && initialEndpoint && (
          <Button
            onClick={(): Promise<void> => onTestSend()}
            disabled={saving || testing}
            sx={[secondaryButtonBackgroundColor, styles.buttonLarge]}
          >
            {WEBHOOK_TEST_SEND_BUTTON}
          </Button>
        )}
        {initialEndpoint && (
          <Button
            onClick={(): void => {
              if (window.confirm(WEBHOOK_DELETE_CONFIRM)) {
                void onDelete();
              }
            }}
            disabled={saving}
            sx={[tertiaryButtonBackgroundColor, styles.buttonLarge, { ml: 'auto' }]}
          >
            {WEBHOOK_DELETE_BUTTON}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default WebhookEndpointForm;
